import { useEffect, useMemo, useState } from 'react'
import { formatPaise, gstBreakdown, toPaise } from '@pharmacia/core'
import { ApiError, del, get, patch, post } from '../lib/api.ts'
import { useList } from '../lib/useList.ts'
import type { UploadedFile } from '../lib/upload.ts'
import { PageHeader } from '../components/AppShell.tsx'
import { Modal } from '../components/Modal.tsx'
import { ConfirmDialog } from '../components/ConfirmDialog.tsx'
import { CameraCapture } from '../components/CameraCapture.tsx'
import { useToast } from '../components/Toast.tsx'
import { Button, Empty, Field, Input, SkeletonRows } from '../components/ui.tsx'
import { IconEdit, IconPlus, IconSearch, IconTrash } from '../components/icons.tsx'

type Product = {
  id: string; name: string; description: string | null
  imagePath: string | null
  price: string; cgstRate: string; sgstRate: string
  isActive: boolean; version: number
}

/** The owner enters the taxable value; everything else is derived. */
const breakdownOf = (p: { price: string; cgstRate: string; sgstRate: string }) =>
  gstBreakdown(toPaise(p.price), Number(p.cgstRate), Number(p.sgstRate))

export function ProductsPage() {
  const toast = useToast()
  const list = useList<Product>('/api/products')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<Product | null>(null)
  const [removing, setRemoving] = useState<Product | null>(null)

  return (
    <>
      <PageHeader title="Products">
        <Button variant="primary" onClick={() => setAdding(true)}>
          <IconPlus /> Add product
        </Button>
      </PageHeader>

      <div className="content">
        <div className="page-max stack" style={{ gap: 'var(--s-5)' }}>
          <div className="toolbar">
            <div className="search">
              <IconSearch className="ic" />
              <Input placeholder="Search products" value={list.q}
                onChange={e => list.setQ(e.target.value)} aria-label="Search products" />
            </div>
          </div>

          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th /><th>Product</th>
                    <th className="money">Price</th>
                    <th className="money">CGST</th>
                    <th className="money">SGST</th>
                    <th className="money">Total</th>
                    <th><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {list.loading ? <SkeletonRows rows={5} cols={7} /> : null}
                  {!list.loading && list.items.map(p => {
                    const b = breakdownOf(p)
                    return (
                      <tr key={p.id}>
                        <td><ProductThumb product={p} /></td>
                        <td>
                          <div style={{ fontWeight: 550 }}>
                            {p.name}
                            {!p.isActive ? <span className="badge off" style={{ marginLeft: 8 }}>Inactive</span> : null}
                          </div>
                          {p.description
                            ? <div className="dim truncate" style={{ fontSize: 'var(--text-xs)', maxWidth: 380 }}>
                                {p.description}
                              </div>
                            : null}
                        </td>
                        <td className="money">{formatPaise(b.base)}</td>
                        <td className="money dim">{formatPaise(b.cgst)}<span style={{ fontSize: 'var(--text-xs)' }}> · {Number(p.cgstRate)}%</span></td>
                        <td className="money dim">{formatPaise(b.sgst)}<span style={{ fontSize: 'var(--text-xs)' }}> · {Number(p.sgstRate)}%</span></td>
                        <td className="money" style={{ fontWeight: 650 }}>{formatPaise(b.total)}</td>
                        <td className="actions">
                          <span className="row-actions">
                            <button className="icon-btn" title="Edit" aria-label={`Edit ${p.name}`}
                              onClick={() => setEditing(p)}><IconEdit /></button>
                            <button className="icon-btn danger" title="Delete" aria-label={`Delete ${p.name}`}
                              onClick={() => setRemoving(p)}><IconTrash /></button>
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {!list.loading && list.items.length === 0 ? (
              <Empty title={list.q ? 'No matches' : 'No products yet'}>
                {list.q ? 'Try a different name.' : 'Add the first product and its price.'}
              </Empty>
            ) : null}

            {list.error ? (
              <div className="empty">
                <p style={{ color: 'var(--danger)' }}>{list.error}</p>
                <Button onClick={list.refresh} style={{ marginTop: 'var(--s-3)' }}>Try again</Button>
              </div>
            ) : null}
          </div>

          {list.hasMore ? (
            <div className="row" style={{ justifyContent: 'center' }}>
              <Button onClick={list.loadMore} loading={list.loadingMore}>Load more</Button>
            </div>
          ) : null}

          <p className="dim" style={{ fontSize: 'var(--text-xs)', maxWidth: '66ch', lineHeight: 1.7 }}>
            Prices are entered excluding tax. CGST and SGST are each rounded
            separately, so the printed total always equals the sum of the lines
            above it. Intra-state supply — IGST would apply instead for a
            patient outside Rajasthan.
          </p>
        </div>
      </div>

      {adding ? (
        <ProductForm onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); list.refresh(); toast.ok('Product added') }} />
      ) : null}

      {editing ? (
        <ProductForm existing={editing} onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.refresh(); toast.ok('Product updated') }} />
      ) : null}

      {removing ? (
        <ConfirmDialog
          title={`Delete ${removing.name}?`}
          body="The product is kept, not erased — anything already billed against it still refers to it. It stops appearing in this list."
          confirmLabel="Delete" danger
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            try {
              await del(`/api/products/${removing.id}`)
              list.refresh()
              toast.ok('Product deleted')
            } catch (e) { toast.fail('Could not delete', (e as ApiError).message) }
          }}
        />
      ) : null}
    </>
  )
}

function ProductThumb({ product }: { product: Product }) {
  const [url, setUrl] = useState<string | null>(null)

  // In an effect, not during render. Setting state while rendering is what
  // turns a list of twenty products into twenty render loops.
  useEffect(() => {
    if (!product.imagePath) return
    let cancelled = false
    get<{ url: string }>(`/api/products/${product.id}/image`)
      .then(r => { if (!cancelled) setUrl(r.url) })
      .catch(() => { /* the row is still usable without a picture */ })
    return () => { cancelled = true }
  }, [product.id, product.imagePath])

  if (!product.imagePath) return <div className="thumb">—</div>
  return <div className="thumb">{url ? <img src={url} alt="" /> : null}</div>
}

// ------------------------------------------------------------ add / edit
function ProductForm({ existing, onClose, onSaved }: {
  existing?: Product; onClose: () => void; onSaved: () => void
}) {
  const toast = useToast()
  const editMode = Boolean(existing)
  const [name, setName] = useState(existing?.name ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [price, setPrice] = useState(existing?.price ?? '')
  const [cgstRate, setCgst] = useState(existing?.cgstRate ?? '2.5')
  const [sgstRate, setSgst] = useState(existing?.sgstRate ?? '2.5')
  const [image, setImage] = useState<UploadedFile | null>(null)
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Fixed for the life of this form. Generating it at submit time -- or
  // twice -- would mean a retry after a lost response creates a second
  // line in the price list rather than returning the first.
  const newId = useMemo(() => crypto.randomUUID(), [])

  const valid = /^\d{1,9}(\.\d{1,2})?$/.test(price.trim())
  const preview = valid
    ? gstBreakdown(toPaise(price), Number(cgstRate) || 0, Number(sgstRate) || 0)
    : null

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErrors({})
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        price: price.trim(),
        cgstRate: Number(cgstRate),
        sgstRate: Number(sgstRate),
        ...(image ? { imagePath: image.path } : {}),
      }
      if (existing) {
        await patch(`/api/products/${existing.id}`, { ...body, version: existing.version })
      } else {
        await post('/api/products', { id: newId, ...body })
      }
      onSaved()
    } catch (e) {
      const err = e as ApiError
      if (err.code === 'VALIDATION_FAILED') setErrors(err.fieldErrors)
      else if (err.code === 'STALE_WRITE') toast.fail('Someone else saved first', err.message)
      else if (err.code === 'CONFLICT') setErrors({ name: err.message })
      else toast.fail(editMode ? 'Could not save' : 'Could not add', err.message)
    } finally { setBusy(false) }
  }

  return (
    <Modal
      title={editMode ? `Edit ${existing!.name}` : 'Add product'}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <Button type="button" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="product-form" variant="primary" loading={busy}>
            {editMode ? 'Save changes' : 'Add product'}
          </Button>
        </>
      }
    >
      <form id="product-form" onSubmit={submit} className="stack" style={{ gap: 'var(--s-4)' }}>
        <Field label="Name" htmlFor="pr-name" error={errors['name']}>
          <Input id="pr-name" value={name} onChange={e => setName(e.target.value)}
            required maxLength={160} autoFocus aria-invalid={Boolean(errors['name'])} />
        </Field>

        <Field label="Description" htmlFor="pr-desc" error={errors['description']} hint="Optional">
          <Input id="pr-desc" value={description} onChange={e => setDescription(e.target.value)}
            maxLength={1000} />
        </Field>

        <div className="form-grid">
          <Field label="Price before tax" htmlFor="pr-price" error={errors['price']}
            hint="Rupees, e.g. 13.33">
            <Input id="pr-price" value={price} onChange={e => setPrice(e.target.value)}
              inputMode="decimal" required aria-invalid={Boolean(errors['price'])} />
          </Field>
          <div />
          <Field label="CGST %" htmlFor="pr-cgst" error={errors['cgstRate']}>
            <Input id="pr-cgst" value={cgstRate} onChange={e => setCgst(e.target.value)}
              inputMode="decimal" />
          </Field>
          <Field label="SGST %" htmlFor="pr-sgst" error={errors['sgstRate']}>
            <Input id="pr-sgst" value={sgstRate} onChange={e => setSgst(e.target.value)}
              inputMode="decimal" />
          </Field>
        </div>

        {preview ? (
          <div className="price-preview">
            <span className="lbl">Price before tax</span>
            <span className="amt">{formatPaise(preview.base)}</span>
            <span className="lbl">CGST {Number(cgstRate) || 0}%</span>
            <span className="amt">{formatPaise(preview.cgst)}</span>
            <span className="lbl">SGST {Number(sgstRate) || 0}%</span>
            <span className="amt">{formatPaise(preview.sgst)}</span>
            <span className="lbl sum">Selling price</span>
            <span className="amt sum">{formatPaise(preview.total)}</span>
          </div>
        ) : null}

        <Field label="Image" hint="Optional — capture from any attached camera, or upload a file">
          <CameraCapture kind="product" aspect="1 / 1" value={image} onChange={setImage} />
        </Field>
      </form>
    </Modal>
  )
}
